'use client'

import ConfirmModal from '@/components/confirm-modal'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import {
    CreateReferralData,
    MEDICAL_SPECIALTY_LABELS,
    MedicalSpecialty,
    REFERRAL_STATUS_LABELS,
    REFERRAL_URGENCY_COLORS,
    REFERRAL_URGENCY_LABELS,
    Referral,
    ReferralStatus,
    ReferralUrgency,
    UpdateReferralData,
    createReferral,
    deleteReferral,
    getReferralsByClient,
    printReferralPdf,
    updateReferral,
    updateReferralStatus,
} from '@/lib/api/prontuario/prontuario-api'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdCheck,
    MdClose,
    MdDelete,
    MdEdit,
    MdPerson,
    MdPrint,
    MdSend,
    MdVisibility,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioData } from './prontuario-context'

type DestinationType = 'partner' | 'manual'

interface ReferralFormData {
    professionalId: string
    specialty: MedicalSpecialty
    urgency: ReferralUrgency
    reason: string
    clinicalSummary: string
    relevantExams: string
    currentMedications: string
    destinationType: DestinationType
    partnerId: number | null
    externalProfessionalName: string
    externalProfessionalCrm: string
    externalClinicName: string
    externalClinicAddress: string
    externalClinicPhone: string
    scheduledDate: string
}

const defaultFormData: ReferralFormData = {
    professionalId: '',
    specialty: MedicalSpecialty.GENERAL_PRACTICE,
    urgency: ReferralUrgency.ROUTINE,
    reason: '',
    clinicalSummary: '',
    relevantExams: '',
    currentMedications: '',
    destinationType: 'partner',
    partnerId: null,
    externalProfessionalName: '',
    externalProfessionalCrm: '',
    externalClinicName: '',
    externalClinicAddress: '',
    externalClinicPhone: '',
    scheduledDate: '',
}

export default function EncaminhamentosPage() {
    const { client, clientId } = useProntuarioData()
    const { user, isProfessional } = useAuth()

    const [referrals, setReferrals] = useState<Referral[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [detailModalOpen, setDetailModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [formData, setFormData] = useState<ReferralFormData>(defaultFormData)
    const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [referralToDelete, setReferralToDelete] = useState<number | null>(null)
    const [printing, setPrinting] = useState<number | null>(null)

    useEffect(() => {
        if (clientId) {
            loadReferrals()
        }
    }, [clientId, user?.companyId])

    const loadReferrals = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const response = await getReferralsByClient(parseInt(clientId, 10))
            if (response.success) {
                setReferrals(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar encaminhamentos')
            }
        } catch (error) {
            toast.error('Erro ao carregar encaminhamentos')
        } finally {
            setLoading(false)
        }
    }

    const openModal = (referral?: Referral) => {
        if (referral) {
            setEditingId(referral.id)
            setFormData({
                professionalId: referral.referringProfessional?.id?.toString() || referral.referringProfessionalId?.toString() || '',
                specialty: referral.specialty,
                urgency: referral.urgency,
                reason: referral.reason,
                clinicalSummary: referral.clinicalSummary || '',
                relevantExams: referral.relevantExams || '',
                currentMedications: referral.currentMedications || '',
                destinationType: referral.partnerId ? 'partner' : 'manual',
                partnerId: referral.partnerId || null,
                externalProfessionalName: referral.externalProfessionalName || '',
                externalProfessionalCrm: referral.externalProfessionalCrm || '',
                externalClinicName: referral.externalClinicName || '',
                externalClinicAddress: referral.externalClinicAddress || '',
                externalClinicPhone: referral.externalClinicPhone || '',
                scheduledDate: referral.scheduledDate ? referral.scheduledDate.split('T')[0] : '',
            })
        } else {
            setEditingId(null)
            setFormData(defaultFormData)
        }
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setEditingId(null)
        setFormData(defaultFormData)
    }

    const openDetailModal = (referral: Referral) => {
        setSelectedReferral(referral)
        setDetailModalOpen(true)
    }

    const closeDetailModal = () => {
        setDetailModalOpen(false)
        setSelectedReferral(null)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!isProfessional && !formData.professionalId) {
            toast.error('Selecione um profissional solicitante')
            return
        }

        if (!formData.reason.trim()) {
            toast.error('Informe o motivo do encaminhamento')
            return
        }

        if (!clientId) return

        setSaving(true)
        try {
            const isPartner = formData.destinationType === 'partner'

            if (editingId) {
                const updateData: UpdateReferralData = {
                    specialty: formData.specialty,
                    urgency: formData.urgency,
                    reason: formData.reason.trim(),
                    clinicalSummary: formData.clinicalSummary.trim() || undefined,
                    relevantExams: formData.relevantExams.trim() || undefined,
                    currentMedications: formData.currentMedications.trim() || undefined,
                    partnerId: isPartner ? formData.partnerId : null,
                    externalProfessionalName: isPartner ? undefined : formData.externalProfessionalName.trim() || undefined,
                    externalProfessionalCrm: isPartner ? undefined : formData.externalProfessionalCrm.trim() || undefined,
                    externalClinicName: isPartner ? undefined : formData.externalClinicName.trim() || undefined,
                    externalClinicAddress: isPartner ? undefined : formData.externalClinicAddress.trim() || undefined,
                    externalClinicPhone: isPartner ? undefined : formData.externalClinicPhone.trim() || undefined,
                    scheduledDate: formData.scheduledDate || undefined,
                }

                const response = await updateReferral(editingId, updateData)
                if (response.success) {
                    toast.success('Encaminhamento atualizado com sucesso!')
                    await loadReferrals()
                    closeModal()
                } else {
                    toast.error(response.error || 'Erro ao atualizar encaminhamento')
                }
            } else {
                const createData: CreateReferralData = {
                    clientId: parseInt(clientId, 10),
                    companyId: user!.companyId!,
                    referringProfessionalId: isProfessional ? user!.professionalId! : parseInt(formData.professionalId, 10),
                    specialty: formData.specialty,
                    urgency: formData.urgency,
                    reason: formData.reason.trim(),
                    clinicalSummary: formData.clinicalSummary.trim() || undefined,
                    relevantExams: formData.relevantExams.trim() || undefined,
                    currentMedications: formData.currentMedications.trim() || undefined,
                    partnerId: isPartner ? formData.partnerId || undefined : undefined,
                    externalProfessionalName: isPartner ? undefined : formData.externalProfessionalName.trim() || undefined,
                    externalProfessionalCrm: isPartner ? undefined : formData.externalProfessionalCrm.trim() || undefined,
                    externalClinicName: isPartner ? undefined : formData.externalClinicName.trim() || undefined,
                    externalClinicAddress: isPartner ? undefined : formData.externalClinicAddress.trim() || undefined,
                    externalClinicPhone: isPartner ? undefined : formData.externalClinicPhone.trim() || undefined,
                    scheduledDate: formData.scheduledDate || undefined,
                }

                const response = await createReferral(createData)
                if (response.success) {
                    toast.success('Encaminhamento criado com sucesso!')
                    await loadReferrals()
                    closeModal()
                } else {
                    toast.error(response.error || 'Erro ao criar encaminhamento')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar encaminhamento')
        } finally {
            setSaving(false)
        }
    }

    const handleStatusChange = async (id: number, status: ReferralStatus) => {
        try {
            const response = await updateReferralStatus(id, status)
            if (response.success) {
                toast.success('Status atualizado com sucesso!')
                await loadReferrals()
            } else {
                toast.error(response.error || 'Erro ao atualizar status')
            }
        } catch (error) {
            toast.error('Erro ao atualizar status')
        }
    }

    const handlePrint = async (id: number) => {
        setPrinting(id)
        try {
            await printReferralPdf(id)
            toast.success('PDF gerado com sucesso!')
            await loadReferrals()
        } catch (error) {
            toast.error('Erro ao gerar PDF para impressão')
        } finally {
            setPrinting(null)
        }
    }

    const handleDelete = (id: number) => {
        setReferralToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!referralToDelete) return

        try {
            const response = await deleteReferral(referralToDelete)
            if (response.success) {
                toast.success('Encaminhamento excluído com sucesso!')
                await loadReferrals()
            } else {
                toast.error(response.error || 'Erro ao excluir encaminhamento')
            }
        } catch (error) {
            toast.error('Erro ao excluir encaminhamento')
        } finally {
            setShowDeleteModal(false)
            setReferralToDelete(null)
        }
    }

    const getStatusColor = (status: ReferralStatus): string => {
        switch (status) {
            case ReferralStatus.REFERRAL_PENDING:
                return 'badge-warning'
            case ReferralStatus.REFERRAL_SCHEDULED:
                return 'badge-info'
            case ReferralStatus.REFERRAL_IN_PROGRESS:
                return 'badge-secondary'
            case ReferralStatus.REFERRAL_COMPLETED:
                return 'badge-success'
            case ReferralStatus.REFERRAL_CANCELLED:
            case ReferralStatus.REFERRAL_NO_SHOW:
                return 'badge-error'
            default:
                return 'badge-ghost'
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando encaminhamentos...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-base-content">Encaminhamentos</h2>
                    <p className="text-sm text-base-content/60">
                        Gerencie os encaminhamentos do paciente
                    </p>
                </div>
                <button onClick={() => openModal()} className="btn btn-secondary gap-2">
                    <MdAdd className="text-lg" />
                    Novo Encaminhamento
                </button>
            </div>

            {/* Referrals List */}
            {referrals.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdSend className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">Nenhum encaminhamento registrado</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {referrals.map(referral => (
                        <div
                            key={referral.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden"
                        >
                            <div className="p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="p-2 bg-secondary/10 rounded-lg">
                                                <MdSend className="text-xl text-secondary" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-base-content">
                                                    {MEDICAL_SPECIALTY_LABELS[referral.specialty]}
                                                </h3>
                                                <p className="text-sm text-base-content/60">
                                                    {format(new Date(referral.referralDate), 'dd/MM/yyyy', { locale: ptBR })}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-3">
                                            <span className={`badge ${REFERRAL_URGENCY_COLORS[referral.urgency]}`}>
                                                {REFERRAL_URGENCY_LABELS[referral.urgency]}
                                            </span>
                                            <span className={`badge ${getStatusColor(referral.status)}`}>
                                                {REFERRAL_STATUS_LABELS[referral.status]}
                                            </span>
                                        </div>

                                        <p className="text-sm text-base-content/70 line-clamp-2 mb-3">
                                            {referral.reason}
                                        </p>

                                        {(referral.partner || referral.externalClinicName) && (
                                            <p className="text-sm text-base-content/60">
                                                Destino: {referral.partner
                                                    ? (referral.partner.name || referral.partner.tradingName)
                                                    : referral.externalClinicName}
                                            </p>
                                        )}

                                        {referral.referringProfessional && (
                                            <div className="flex items-center gap-2 text-xs text-base-content/50 mt-3">
                                                <MdPerson className="text-sm" />
                                                <span>Solicitante: {referral.referringProfessional.name}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col items-end gap-2">
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => handlePrint(referral.id)}
                                                className="btn btn-ghost btn-sm btn-square"
                                                title="Imprimir PDF"
                                                disabled={printing === referral.id}
                                            >
                                                {printing === referral.id ? (
                                                    <span className="loading loading-spinner loading-sm"></span>
                                                ) : (
                                                    <MdPrint className="text-lg" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => openDetailModal(referral)}
                                                className="btn btn-ghost btn-sm btn-square"
                                                title="Detalhes"
                                            >
                                                <MdVisibility className="text-lg" />
                                            </button>
                                            <button
                                                onClick={() => openModal(referral)}
                                                className="btn btn-ghost btn-sm btn-square"
                                                title="Editar"
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(referral.id)}
                                                className="btn btn-ghost btn-sm btn-square text-error"
                                                title="Excluir"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        </div>

                                        {/* Status Update Dropdown */}
                                        <div className="dropdown dropdown-end">
                                            <label tabIndex={0} className="btn btn-xs btn-ghost">
                                                Atualizar Status
                                            </label>
                                            <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52">
                                                {Object.entries(REFERRAL_STATUS_LABELS).map(([key, label]) => (
                                                    <li key={key}>
                                                        <button
                                                            onClick={() => handleStatusChange(referral.id, key as ReferralStatus)}
                                                            className={referral.status === key ? 'active' : ''}
                                                        >
                                                            {label}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {modalOpen && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">
                                {editingId ? 'Editar Encaminhamento' : 'Novo Encaminhamento'}
                            </h3>
                            <button onClick={closeModal} className="btn btn-ghost btn-sm btn-square">
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {!isProfessional && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Profissional Solicitante <span className="text-error">*</span></span>
                                    </label>
                                    <ProfessionalSearchSelector
                                        value={formData.professionalId ? Number(formData.professionalId) : null}
                                        onChange={(id) => setFormData(prev => ({ ...prev, professionalId: id ? String(id) : '' }))}
                                        label=""
                                        placeholder="Selecione..."
                                        disabled={!!editingId}
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Especialidade <span className="text-error">*</span></span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formData.specialty}
                                        onChange={e => setFormData(prev => ({ ...prev, specialty: e.target.value as MedicalSpecialty }))}
                                    >
                                        {Object.entries(MEDICAL_SPECIALTY_LABELS).map(([key, label]) => (
                                            <option key={key} value={key}>{label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Urgência</span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formData.urgency}
                                        onChange={e => setFormData(prev => ({ ...prev, urgency: e.target.value as ReferralUrgency }))}
                                    >
                                        {Object.entries(REFERRAL_URGENCY_LABELS).map(([key, label]) => (
                                            <option key={key} value={key}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Motivo do Encaminhamento <span className="text-error">*</span></span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Descreva o motivo do encaminhamento..."
                                    rows={3}
                                    value={formData.reason}
                                    onChange={e => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                                    required
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Resumo Clínico</span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Resumo da história clínica do paciente..."
                                    rows={3}
                                    value={formData.clinicalSummary}
                                    onChange={e => setFormData(prev => ({ ...prev, clinicalSummary: e.target.value }))}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Exames Relevantes</span>
                                    </label>
                                    <textarea
                                        className="textarea textarea-bordered w-full"
                                        placeholder="Lista de exames já realizados..."
                                        rows={2}
                                        value={formData.relevantExams}
                                        onChange={e => setFormData(prev => ({ ...prev, relevantExams: e.target.value }))}
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Medicamentos em Uso</span>
                                    </label>
                                    <textarea
                                        className="textarea textarea-bordered w-full"
                                        placeholder="Lista de medicamentos..."
                                        rows={2}
                                        value={formData.currentMedications}
                                        onChange={e => setFormData(prev => ({ ...prev, currentMedications: e.target.value }))}
                                    />
                                </div>
                            </div>

                            <div className="divider">Profissional / Clínica de Destino (opcional)</div>

                            <div className="tabs tabs-boxed mb-4">
                                <button
                                    type="button"
                                    className={`tab ${formData.destinationType === 'partner' ? 'tab-active' : ''}`}
                                    onClick={() => setFormData(prev => ({ ...prev, destinationType: 'partner' }))}
                                >
                                    Parceiro Cadastrado
                                </button>
                                <button
                                    type="button"
                                    className={`tab ${formData.destinationType === 'manual' ? 'tab-active' : ''}`}
                                    onClick={() => setFormData(prev => ({ ...prev, destinationType: 'manual' }))}
                                >
                                    Informar Manualmente
                                </button>
                            </div>

                            {formData.destinationType === 'partner' ? (
                                <PartnerSelector
                                    value={formData.partnerId}
                                    onChange={(partnerId) => setFormData(prev => ({ ...prev, partnerId }))}
                                    label="Parceiro de Destino"
                                    placeholder="Buscar parceiro cadastrado..."
                                />
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text font-medium">Nome do Profissional</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered w-full"
                                                placeholder="Dr. Nome..."
                                                value={formData.externalProfessionalName}
                                                onChange={e => setFormData(prev => ({ ...prev, externalProfessionalName: e.target.value }))}
                                            />
                                        </div>

                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text font-medium">CRM</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered w-full"
                                                placeholder="CRM/UF"
                                                value={formData.externalProfessionalCrm}
                                                onChange={e => setFormData(prev => ({ ...prev, externalProfessionalCrm: e.target.value }))}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text font-medium">Nome da Clínica</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered w-full"
                                                placeholder="Clínica..."
                                                value={formData.externalClinicName}
                                                onChange={e => setFormData(prev => ({ ...prev, externalClinicName: e.target.value }))}
                                            />
                                        </div>

                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text font-medium">Telefone</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered w-full"
                                                placeholder="(00) 0000-0000"
                                                value={formData.externalClinicPhone}
                                                onChange={e => setFormData(prev => ({ ...prev, externalClinicPhone: e.target.value }))}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">Endereço da Clínica</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Endereço completo..."
                                            value={formData.externalClinicAddress}
                                            onChange={e => setFormData(prev => ({ ...prev, externalClinicAddress: e.target.value }))}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="form-control w-full max-w-xs">
                                <label className="label">
                                    <span className="label-text font-medium">Data Agendada</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.scheduledDate}
                                    onChange={e => setFormData(prev => ({ ...prev, scheduledDate: e.target.value }))}
                                />
                            </div>

                            <div className="modal-action">
                                <button type="button" onClick={closeModal} className="btn btn-ghost" disabled={saving}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-secondary gap-2" disabled={saving}>
                                    {saving ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm"></span>
                                            Salvando...
                                        </>
                                    ) : (
                                        <>
                                            <MdCheck className="text-lg" />
                                            Salvar
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                    <div className="modal-backdrop" onClick={closeModal}></div>
                </div>
            )}

            {/* Detail Modal */}
            {detailModalOpen && selectedReferral && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">Detalhes do Encaminhamento</h3>
                            <button onClick={closeDetailModal} className="btn btn-ghost btn-sm btn-square">
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2">
                                <span className={`badge badge-lg ${REFERRAL_URGENCY_COLORS[selectedReferral.urgency]}`}>
                                    {REFERRAL_URGENCY_LABELS[selectedReferral.urgency]}
                                </span>
                                <span className={`badge badge-lg ${getStatusColor(selectedReferral.status)}`}>
                                    {REFERRAL_STATUS_LABELS[selectedReferral.status]}
                                </span>
                            </div>

                            <div>
                                <h4 className="font-semibold text-base-content mb-1">Especialidade</h4>
                                <p className="text-base-content/70">{MEDICAL_SPECIALTY_LABELS[selectedReferral.specialty]}</p>
                            </div>

                            <div>
                                <h4 className="font-semibold text-base-content mb-1">Motivo</h4>
                                <p className="text-base-content/70">{selectedReferral.reason}</p>
                            </div>

                            {selectedReferral.clinicalSummary && (
                                <div>
                                    <h4 className="font-semibold text-base-content mb-1">Resumo Clínico</h4>
                                    <p className="text-base-content/70">{selectedReferral.clinicalSummary}</p>
                                </div>
                            )}

                            {selectedReferral.relevantExams && (
                                <div>
                                    <h4 className="font-semibold text-base-content mb-1">Exames Relevantes</h4>
                                    <p className="text-base-content/70">{selectedReferral.relevantExams}</p>
                                </div>
                            )}

                            {selectedReferral.currentMedications && (
                                <div>
                                    <h4 className="font-semibold text-base-content mb-1">Medicamentos em Uso</h4>
                                    <p className="text-base-content/70">{selectedReferral.currentMedications}</p>
                                </div>
                            )}

                            {(selectedReferral.partner || selectedReferral.externalClinicName || selectedReferral.externalProfessionalName) && (
                                <div className="divider">Destino</div>
                            )}

                            {selectedReferral.partner ? (
                                <div>
                                    <h4 className="font-semibold text-base-content mb-1">Parceiro</h4>
                                    <p className="text-base-content/70">
                                        {selectedReferral.partner.name || selectedReferral.partner.tradingName}
                                    </p>
                                    {selectedReferral.partner.phone && (
                                        <p className="text-base-content/60 text-sm">Tel: {selectedReferral.partner.phone}</p>
                                    )}
                                    {selectedReferral.partner.email && (
                                        <p className="text-base-content/60 text-sm">Email: {selectedReferral.partner.email}</p>
                                    )}
                                    {(selectedReferral.partner.street || selectedReferral.partner.city) && (
                                        <p className="text-base-content/60 text-sm">
                                            {[
                                                selectedReferral.partner.street,
                                                selectedReferral.partner.number,
                                                selectedReferral.partner.neighborhood,
                                                selectedReferral.partner.city,
                                                selectedReferral.partner.state,
                                            ].filter(Boolean).join(', ')}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {selectedReferral.externalProfessionalName && (
                                        <div>
                                            <h4 className="font-semibold text-base-content mb-1">Profissional</h4>
                                            <p className="text-base-content/70">
                                                {selectedReferral.externalProfessionalName}
                                                {selectedReferral.externalProfessionalCrm && ` - ${selectedReferral.externalProfessionalCrm}`}
                                            </p>
                                        </div>
                                    )}

                                    {selectedReferral.externalClinicName && (
                                        <div>
                                            <h4 className="font-semibold text-base-content mb-1">Clínica</h4>
                                            <p className="text-base-content/70">{selectedReferral.externalClinicName}</p>
                                            {selectedReferral.externalClinicAddress && (
                                                <p className="text-base-content/60 text-sm">{selectedReferral.externalClinicAddress}</p>
                                            )}
                                            {selectedReferral.externalClinicPhone && (
                                                <p className="text-base-content/60 text-sm">{selectedReferral.externalClinicPhone}</p>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            <div className="divider">Datas</div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-base-content/60">Data do Encaminhamento:</span>
                                    <p className="font-medium">{format(new Date(selectedReferral.referralDate), 'dd/MM/yyyy', { locale: ptBR })}</p>
                                </div>
                                {selectedReferral.scheduledDate && (
                                    <div>
                                        <span className="text-base-content/60">Data Agendada:</span>
                                        <p className="font-medium">{format(new Date(selectedReferral.scheduledDate), 'dd/MM/yyyy', { locale: ptBR })}</p>
                                    </div>
                                )}
                                {selectedReferral.completedDate && (
                                    <div>
                                        <span className="text-base-content/60">Data de Conclusão:</span>
                                        <p className="font-medium">{format(new Date(selectedReferral.completedDate), 'dd/MM/yyyy', { locale: ptBR })}</p>
                                    </div>
                                )}
                            </div>

                            {selectedReferral.feedback && (
                                <div>
                                    <h4 className="font-semibold text-base-content mb-1">Feedback</h4>
                                    <p className="text-base-content/70">{selectedReferral.feedback}</p>
                                </div>
                            )}
                        </div>

                        <div className="modal-action">
                            <button onClick={closeDetailModal} className="btn btn-ghost">Fechar</button>
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={closeDetailModal}></div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setReferralToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir encaminhamento"
                message="Tem certeza que deseja excluir este encaminhamento?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
